import { useEffect, useState, useRef } from 'react'
import { motion } from 'framer-motion'

const PersonalHero = () => {
  const [displayText, setDisplayText] = useState('')
  const [showCursor, setShowCursor] = useState(true)
  const [typingDone, setTypingDone] = useState(false)
  const fullText = 'I build complete systems'
  const typingSpeed = 45

  useEffect(() => {
    let i = 0
    const timer = setInterval(() => {
      if (i < fullText.length) {
        setDisplayText(fullText.slice(0, i + 1))
        i++
      } else {
        clearInterval(timer)
        setTypingDone(true)
      }
    }, typingSpeed)

    return () => clearInterval(timer)
  }, [])

  // Blinking cursor
  useEffect(() => {
    if (!typingDone) return
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev)
    }, 530)
    return () => clearInterval(cursorTimer)
  }, [typingDone])

  const stats = [
    { value: '14k+', label: 'Users on Platform', color: '#00FFE0' },
    { value: '50+', label: 'Branches Served', color: '#4ADE80' },
    { value: '12+', label: 'Data Sources Integrated', color: '#A78BFA' }
  ]

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: 'var(--bg-deep)' }}>
      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,255,224,0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,255,224,0.3) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Radial glow */}
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(0,255,224,0.08) 0%, transparent 70%)'
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl">

          {/* Terminal prompt */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <span className="font-mono text-sm text-[#555570]">
              <span className="text-[#00FFE0]">ishay</span>
              <span className="text-[#555570]">@</span>
              <span className="text-[#8888A0]">builder</span>
              <span className="text-[#555570]">:</span>
              <span className="text-[#60A5FA]">~</span>
              <span className="text-[#555570]">$</span>
            </span>
          </motion.div>

          {/* Main typed title */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="mb-8"
          >
            <h1 className="font-mono text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#555570] mr-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl">&gt;</span>
              <span className="text-[#E0E0E8]">{displayText}</span>
              <span
                className={`inline-block w-[3px] h-[0.9em] ml-1 align-middle transition-opacity duration-100 ${
                  showCursor ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ backgroundColor: 'var(--accent)' }}
              />
            </h1>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={typingDone ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-lg md:text-xl leading-relaxed max-w-2xl mb-10"
            style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}
          >
            I design and own production systems end-to-end. Backend platforms, business intelligence, AI tools, open source — I build the infrastructure that organizations run on.
          </motion.p>

          {/* Comment-style stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={typingDone ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-mono text-xs sm:text-sm mb-12 space-y-1"
          >
            <div className="text-[#555570]">
              <span className="text-[#4ADE80]">// </span>
              <span>14k+ users served</span>
              <span className="text-[#555570] mx-1.5 sm:mx-3">|</span>
              <span>50+ branches</span>
              <span className="hidden sm:inline">
                <span className="text-[#555570] mx-3">|</span>
                <span>designed for what breaks at 2AM</span>
              </span>
            </div>
            <div className="text-[#555570] sm:hidden">
              <span className="text-[#4ADE80]">// </span>
              <span>designed for what breaks at 2AM</span>
            </div>
          </motion.div>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={typingDone ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              onClick={() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-terminal-filled"
            >
              <span>See my work</span>
              <span className="text-[#0A0A0F]">&darr;</span>
            </button>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-terminal"
            >
              <span>$ contact --init</span>
            </button>
          </motion.div>
        </div>

        {/* Floating stat cards - right side */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, x: 40 }}
              animate={typingDone ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.15 }}
              className="terminal-card w-52 glow-pulse"
            >
              <div className="p-4">
                <div className="font-mono text-2xl font-bold mb-1" style={{ color: stat.color }}>
                  {stat.value}
                </div>
                <div className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={typingDone ? { opacity: 1 } : {}}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center"
      >
        <div className="font-mono text-xs mb-2" style={{ color: 'var(--text-muted)' }}>scroll</div>
        <div className="w-px h-8 mx-auto" style={{
          background: 'linear-gradient(to bottom, var(--accent), transparent)'
        }} />
      </motion.div>
    </section>
  )
}

export default PersonalHero
